/**
 * useFiscalReadiness — fonte ÚNICA de verdade sobre "a empresa pode emitir NFS-e?".
 *
 * Deriva de: plano (nfse_emission), regime (taxpayer_type), CNPJ, município e
 * certificado A1 (incluindo vencimento). Usado em 3 lugares para ficarem sempre
 * consistentes:
 *  - entry-points (card do dashboard + link em Configurações): `tags` + `canAccess`;
 *  - gerador de NFS-e: `ready` (botão Emitir) + `missing` (banner do que falta).
 *
 * Dois níveis de portão:
 *  - `canAccess` = plano PRO **e** ser emitente (MEI/ME-EPP) → habilita o entry-point.
 *  - `ready`     = `canAccess` **e** CNPJ **e** município **e** cert válido (não vencido)
 *                  → habilita o botão de emitir dentro do gerador.
 */
import { useMemo } from 'react';
import { useCompany } from '@/contexts/useCompany';
import { useFiscalProfile } from '@/hooks/useFiscalProfile';
import { useActiveCertificate } from '@/hooks/useNfse';
import { useFeature } from '@/hooks/useFeature';
import { certExpiryStatus, type CertExpiryLevel } from '@/lib/certExpiry';

/** Tags mostradas nos entry-points quando falta pré-requisito de acesso. */
export type FiscalTag = 'PRO' | 'CNPJ';

export type FiscalMissingKey =
  | 'plan' | 'taxpayer' | 'cnpj' | 'municipio' | 'certificate' | 'certificate_expired';

export interface FiscalMissing {
  key: FiscalMissingKey;
  label: string;
}

export interface FiscalReadiness {
  isLoading: boolean;
  taxpayerType: string | null;
  /** MEI ou ME/EPP (contribuinte que emite NFS-e). */
  isEmitter: boolean;
  planAllows: boolean;
  hasCnpj: boolean;
  hasMunicipio: boolean;
  certLevel: CertExpiryLevel;
  hasValidCert: boolean;
  /** Entry-point habilitado: plano PRO + emitente. */
  canAccess: boolean;
  /** Botão de emitir habilitado: canAccess + CNPJ + município + cert válido. */
  ready: boolean;
  /** Badges do entry-point (vazio quando canAccess). */
  tags: FiscalTag[];
  /** O que falta, em ordem, p/ o banner do gerador. */
  missing: FiscalMissing[];
}

const digits = (s?: string | null) => (s ?? '').replace(/\D/g, '');

/** Entradas cruas p/ derivar o readiness — sem React, exercível por teste puro. */
export interface FiscalReadinessInput {
  /** Plano permite emitir NFS-e (useFeature('nfse_emission').allowed). */
  planAllows: boolean;
  /** Regime do perfil fiscal ('mei' | 'me_epp' | 'autonomo_informal' | null). */
  taxpayerType: string | null;
  /** CNPJ do cadastro da empresa (fonte primária). */
  companyCnpj?: string | null;
  /** CNPJ do perfil fiscal (fallback). */
  profileCnpj?: string | null;
  /** IBGE do município do perfil fiscal. */
  municipioIbge?: string | null;
  /** Existe certificado A1 registrado (independente do vencimento). */
  hasCert: boolean;
  /** Vencimento do certificado (ISO); usado p/ classificar validade. */
  certNotAfter?: string | null;
  profileLoading: boolean;
  certLoading: boolean;
}

/**
 * deriveFiscalReadiness — núcleo PURO da regra "a empresa pode emitir NFS-e?".
 * Separado do hook para ser testável sem montar contextos React. O hook
 * `useFiscalReadiness` só coleta as entradas (company/profile/cert/plano) e delega.
 */
export function deriveFiscalReadiness(input: FiscalReadinessInput): FiscalReadiness {
  const {
    planAllows, taxpayerType, companyCnpj, profileCnpj,
    municipioIbge, hasCert, certNotAfter, profileLoading, certLoading,
  } = input;

  const isEmitter = taxpayerType === 'mei' || taxpayerType === 'me_epp';
  const hasCnpj = digits(companyCnpj || profileCnpj).length === 14;
  const hasMunicipio = digits(municipioIbge).length > 0;
  const certLevel = certExpiryStatus(certNotAfter ?? null).level;
  const hasValidCert = hasCert && certLevel !== 'expired' && certLevel !== 'none';

  const tags: FiscalTag[] = [];
  if (!planAllows) tags.push('PRO');
  if (!isEmitter) tags.push('CNPJ');

  const canAccess = planAllows && isEmitter;
  const ready = canAccess && hasCnpj && hasMunicipio && hasValidCert;

  const missing: FiscalMissing[] = [];
  if (!planAllows) missing.push({ key: 'plan', label: 'Assine o plano PRO para emitir NFS-e.' });
  if (!isEmitter) {
    missing.push({ key: 'taxpayer', label: 'Defina o regime (MEI) e informe o CNPJ nos dados da empresa.' });
  } else {
    if (!hasCnpj) missing.push({ key: 'cnpj', label: 'Informe o CNPJ da empresa.' });
    if (!hasMunicipio) missing.push({ key: 'municipio', label: 'Informe o município da empresa.' });
    if (!hasCert) missing.push({ key: 'certificate', label: 'Envie o certificado digital A1.' });
    else if (certLevel === 'expired') missing.push({ key: 'certificate_expired', label: 'O certificado digital está vencido — envie um novo.' });
  }

  return {
    isLoading: profileLoading || certLoading,
    taxpayerType, isEmitter, planAllows, hasCnpj, hasMunicipio,
    certLevel, hasValidCert, canAccess, ready, tags, missing,
  };
}

export function useFiscalReadiness(): FiscalReadiness {
  const { activeCompany } = useCompany();
  const { profile, isLoading: profLoading } = useFiscalProfile();
  const nfse = useFeature('nfse_emission');
  const { data: cert, isLoading: certLoading } = useActiveCertificate();

  const companyCnpj = (activeCompany as { cnpj_cpf?: string | null } | null)?.cnpj_cpf ?? null;

  return useMemo(() => deriveFiscalReadiness({
    planAllows: nfse.allowed,
    taxpayerType: profile?.taxpayer_type ?? null,
    companyCnpj,
    profileCnpj: profile?.cnpj ?? null,
    municipioIbge: profile?.municipio_ibge ?? null,
    hasCert: Boolean(cert),
    certNotAfter: cert?.not_after ?? null,
    profileLoading: profLoading,
    certLoading,
  }), [companyCnpj, profile, nfse.allowed, cert, profLoading, certLoading]);
}
